import { useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';

import ActionCreators from '~/store/action-creators';

/**
 * Maps an action-creators map so that thunk action creators
 * `(args) => (dispatch, getState) => R`
 * are exposed as their dispatched form
 * `(args) => R`,
 * while plain action creators keep their original signature.
 */
type BoundActionCreators<T> = {
  [K in keyof T]: T[K] extends (
    ...args: infer Args
  ) => (...thunkArgs: any[]) => infer Result
    ? (...args: Args) => Result
    : T[K];
};

/**
 * Architectural boundary with `redux` + `redux-thunk`.
 *
 * `bindActionCreators` in `redux`'s type definitions returns the input map
 * unchanged — it has no knowledge of the thunk middleware that unwraps
 * thunks at dispatch time. In runtime the unwrapping always happens, so the
 * types here are genuinely incomplete relative to behavior.
 *
 * The single `as unknown as` cast below bridges that gap in ONE place so all
 * call sites stay typed correctly (no casts, no `any`, no `@ts-ignore`).
 * Do NOT replicate this cast at call sites — fix it here if it ever needs
 * fixing.
 */
export const useActions = (): BoundActionCreators<typeof ActionCreators> => {
  const dispatch = useDispatch();
  return bindActionCreators(
    ActionCreators,
    dispatch,
  ) as unknown as BoundActionCreators<typeof ActionCreators>;
};
